/**
 * Form Designer — human-friendly field presentation.
 *
 * PostgreSQL stays the source of truth. Nothing here renames a column, edits a
 * schema or changes a binding: this is a PRESENTATION layer that sits between
 * the live catalog (`/api/form-designer/fields/...`) and the Fields panel, so a
 * user can design a form without reading the database.
 *
 * One catalog field becomes one presented field:
 *
 *   databaseColumn  patient_id          the authoritative column, never changed
 *   displayLabel    Patient Name        what the designer shows
 *   dataType        uuid                the live PostgreSQL type
 *   controlType     text                the control a drop still creates
 *   controlLabel    Lookup              the control family, in human words
 *   relationship    patients.id         the foreign key, when the catalog has one
 *   isTechnical     false               keys, audit and system columns are true
 *   isVisible       true                technical fields are off by default
 *   glyph           person              which type icon the row draws
 *
 * `controlType` is deliberately untouched. Relationship fields READ as Lookups
 * and carry their relationship metadata, but dropping one still creates exactly
 * the control it created before — the lookup control itself is a later layer.
 */

import { humanizeField, typeLabel } from "@/lib/tableDesigner";
import { controlTypeForField } from "@/lib/formDesigner";

/** Categories a column falls into. Only the first three are technical. */
export const FIELD_CATEGORY = Object.freeze({
  KEY: "key",
  AUDIT: "audit",
  SYSTEM: "system",
  RELATIONSHIP: "relationship",
  DATA: "data",
});

const TECHNICAL_CATEGORIES = new Set([FIELD_CATEGORY.KEY, FIELD_CATEGORY.AUDIT, FIELD_CATEGORY.SYSTEM]);

/** Audit columns every EMRbyMD table inherits from its mixins. */
const AUDIT_COLUMNS = new Set([
  "created_at",
  "created_by",
  "updated_at",
  "updated_by",
  "deleted_at",
  "deleted_by",
  "version_id",
  "is_demo",
  "updated_by_snapshot",
]);

/** Words that stay upper case when a column name becomes a label. */
const ACRONYMS = new Set([
  "api",
  "cpt",
  "dob",
  "ehr",
  "emr",
  "fhir",
  "hl7",
  "icd",
  "id",
  "json",
  "mrn",
  "ndc",
  "npi",
  "pdf",
  "sms",
  "ssn",
  "uri",
  "url",
  "uuid",
]);

/** Joining words read better lower case unless they open the label. */
const MINOR_WORDS = new Set(["a", "an", "and", "of", "or", "per", "the"]);

const DATE_TYPES = new Set(["date"]);
const TIMESTAMP_TYPES = new Set(["timestamp", "timestamptz"]);
const TIME_TYPES = new Set(["time"]);
const NUMBER_TYPES = new Set(["integer", "bigint", "smallint", "numeric", "real", "double precision"]);

function titleCaseWord(word, index) {
  const lower = word.toLowerCase();
  if (ACRONYMS.has(lower)) return lower.toUpperCase();
  if (index > 0 && MINOR_WORDS.has(lower)) return lower;
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * A column name as a person would write it: `date_of_birth` → `Date of Birth`,
 * `mrn` → `MRN`. Falls back to the raw name when there is nothing to humanize.
 */
export function humanizeColumn(name) {
  const raw = String(name || "").trim();
  if (!raw) return "";
  const words = raw.split(/[_\s]+/).filter(Boolean);
  if (!words.length) return raw;
  return words.map(titleCaseWord).join(" ");
}

/** Crude but safe English singular, used only for display. */
function singularize(word) {
  const lower = String(word || "").toLowerCase();
  if (lower.endsWith("ies") && lower.length > 4) return `${lower.slice(0, -3)}y`;
  if (/(ch|sh|ss|x|z)es$/.test(lower)) return lower.slice(0, -2);
  if (lower.endsWith("s") && !lower.endsWith("ss")) return lower.slice(0, -1);
  return lower;
}

/** `appointments` → `Appointments`. The real table name is never replaced. */
export function tableDisplayName(name) {
  return humanizeColumn(name) || String(name || "");
}

/** `patients` → `Patient`, for a relationship that names its target table. */
export function entityDisplayName(table) {
  return humanizeColumn(singularize(table));
}

function normalizeRelationship(field) {
  const relationship = field?.relationship;
  if (!relationship || !relationship.table) return null;
  return {
    schema: relationship.schema || "public",
    table: relationship.table,
    column: relationship.column || "id",
    displayColumn: relationship.displayColumn || "",
    displayKind: relationship.displayKind || "none",
    displayVia: relationship.displayVia || "",
    secondaryColumn: relationship.secondaryColumn || "",
    constraint: relationship.constraint || "",
    source: relationship.source || "foreign_key",
    tableLabel: tableDisplayName(relationship.table),
    entityLabel: entityDisplayName(relationship.table),
  };
}

/**
 * The label for a relationship column.
 *
 * The entity comes from the column itself (`patient_id` → `Patient`), so a
 * table referenced twice still reads as two different things, and a column
 * that says more than its target (`referred_by` → `Referred By`) keeps saying
 * it. `Name` is added only when the target's own display is a person name
 * assembled elsewhere — `patients` keeps its names in `patient_names`, so
 * `patient_id` reads as `Patient Name` while `location_id` stays `Location`.
 */
function relationshipLabel(column, relationship) {
  const stem = String(column || "").replace(/_id$/, "");
  const base = humanizeColumn(stem) || relationship.entityLabel || humanizeColumn(column);
  if (relationship.displayKind !== "person_name") return base;
  return /\bname$/i.test(base) ? base : `${base} Name`;
}

function classify(field, relationship) {
  const name = String(field?.name || "").toLowerCase();
  const dataType = String(field?.dataType || "").toLowerCase();
  if (field?.primaryKey) return FIELD_CATEGORY.KEY;
  if (AUDIT_COLUMNS.has(name)) return FIELD_CATEGORY.AUDIT;
  if (name.startsWith("fhir_") || name.endsWith("_meta")) return FIELD_CATEGORY.SYSTEM;
  if (relationship) return FIELD_CATEGORY.RELATIONSHIP;
  // A bare UUID reference with no relationship in the catalog cannot be
  // presented as anything a user would recognise, so it is preserved as a
  // technical field rather than guessed at.
  if (dataType === "uuid" && /_id$/.test(name)) return FIELD_CATEGORY.KEY;
  // The catalog's own technical flag catches names this layer does not know.
  // JSONB is excluded: a structured column is content, not plumbing.
  if (field?.technical && dataType !== "jsonb") return FIELD_CATEGORY.SYSTEM;
  return FIELD_CATEGORY.DATA;
}

/** The control family a field reads as. Not the control a drop creates. */
function controlLabelFor(field, relationship, category) {
  const dataType = String(field?.dataType || "").toLowerCase();
  if (relationship) return "Lookup";
  if (field?.lookup?.kind === "static" || field?.lookup?.kind === "reference") return "Choice";
  if (dataType === "uuid") return "Reference";
  if (category === FIELD_CATEGORY.KEY) return "Key";
  if (dataType === "boolean") return "Yes / No";
  if (DATE_TYPES.has(dataType)) return "Date";
  if (TIMESTAMP_TYPES.has(dataType)) return "Date / Time";
  if (TIME_TYPES.has(dataType)) return "Time";
  if (NUMBER_TYPES.has(dataType)) return "Number";
  if (dataType === "jsonb" || dataType === "json") return "Structured";
  return "Text";
}

function glyphFor(field, relationship, category, controlLabel) {
  const dataType = String(field?.dataType || "").toLowerCase();
  if (relationship) return relationship.displayKind === "person_name" ? "person" : "link";
  if (controlLabel === "Choice") return "choice";
  if (category === FIELD_CATEGORY.KEY || dataType === "uuid") return "key";
  if (dataType === "boolean") return "check";
  if (DATE_TYPES.has(dataType) || TIMESTAMP_TYPES.has(dataType)) return "date";
  if (TIME_TYPES.has(dataType)) return "time";
  if (NUMBER_TYPES.has(dataType)) return "number";
  if (dataType === "jsonb" || dataType === "json") return "structured";
  return "text";
}

/**
 * One catalog field as the designer presents it. The catalog entry travels
 * along untouched in `field`, so anything that needs the real column still
 * has it.
 */
export function describeField(field) {
  const databaseColumn = String(field?.name || "");
  const relationship = normalizeRelationship(field);
  const category = classify(field, relationship);
  const controlLabel = controlLabelFor(field, relationship, category);
  const isTechnical = TECHNICAL_CATEGORIES.has(category);
  const displayLabel = relationship
    ? relationshipLabel(databaseColumn, relationship)
    : String(field?.displayLabel || "").trim() || humanizeColumn(databaseColumn);
  return {
    field,
    databaseColumn,
    displayLabel,
    dataType: String(field?.dataType || ""),
    dataTypeLabel: typeLabel(field?.dataType),
    controlType: controlTypeForField(field),
    controlLabel,
    relationship,
    category,
    isTechnical,
    isVisible: !isTechnical,
    glyph: glyphFor(field, relationship, category, controlLabel),
    required: Boolean(field?.required || field?.primaryKey),
    primaryKey: Boolean(field?.primaryKey),
    description: String(field?.description || ""),
  };
}

export function describeFields(fields) {
  return (fields || []).filter((field) => field?.name).map(describeField);
}

/**
 * Search matches what the user sees AND what the database calls it, so
 * `patient` finds `Patient Name` and `patient_id` finds it too.
 */
export function matchesFieldQuery(presented, query) {
  const needle = String(query || "").trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    presented?.displayLabel,
    presented?.databaseColumn,
    String(presented?.databaseColumn || "").replace(/_/g, " "),
    presented?.dataType,
    presented?.dataTypeLabel,
    presented?.controlLabel,
    presented?.relationship?.table,
    presented?.description,
  ];
  return haystack.some((value) => String(value || "").toLowerCase().includes(needle));
}

/** Technical fields are kept, never dropped — they are only held back. */
export function partitionFields(presented) {
  const everyday = [];
  const technical = [];
  (presented || []).forEach((item) => (item.isTechnical ? technical : everyday).push(item));
  return { everyday, technical };
}

/**
 * The technical truth behind a friendly row, for a tooltip. Kept out of the
 * list itself so the normal panel stays readable.
 */
export function fieldDetailLines(presented) {
  if (!presented) return [];
  const lines = [`Database field: ${presented.databaseColumn}`];
  if (presented.dataType) lines.push(`Type: ${presented.dataType.toUpperCase()}`);
  if (presented.relationship) {
    lines.push(`Related table: ${presented.relationship.table}`);
    lines.push(`Related field: ${presented.relationship.column}`);
    const display = presented.relationship.displayVia || presented.relationship.displayColumn;
    if (display) lines.push(`Shows: ${display}`);
    if (presented.relationship.secondaryColumn) {
      lines.push(`Also identified by: ${presented.relationship.secondaryColumn}`);
    }
  }
  lines.push(`Control: ${presented.controlLabel}`);
  if (presented.primaryKey) lines.push("Primary key");
  if (presented.required && !presented.primaryKey) lines.push("Required");
  if (presented.isTechnical) lines.push("Technical field");
  if (presented.description) lines.push(presented.description);
  return lines;
}

export function fieldTooltip(presented) {
  return fieldDetailLines(presented).join("\n");
}
